import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export const SubmitTrackDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({
    track_name: "",
    artist_name: "",
    track_url: "",
    submitter: "",
    rights_confirmed: false,
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.track_name.trim() || !form.artist_name.trim() || !form.track_url.trim()) {
      toast.error("Track name, artist, and link are required.");
      return;
    }
    if (!form.rights_confirmed) {
      toast.error("You must confirm you have the rights to share this track.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/tracks/submit`, form);
      toast.success("Submitted — pending Curb's approval.");
      setForm({ track_name: "", artist_name: "", track_url: "", submitter: "", rights_confirmed: false });
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="submit-track-dialog-backdrop"
        >
          <motion.form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            initial={{ y: 24, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 24, scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            data-testid="submit-track-form"
            className="relative w-full max-w-md rounded-2xl border border-[#9945FF]/40 bg-[#0a0714] p-6 shadow-[0_20px_80px_rgba(153,69,255,0.4)]"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 text-zinc-500 hover:text-[#14F195] p-1 transition-colors"
              data-testid="submit-track-close-button"
            >
              <X size={20} />
            </button>

            <p className="font-bebas tracking-[0.35em] text-[#9945FF] text-sm">STORM SETLIST</p>
            <h3 className="font-marker text-3xl gradient-sol text-glow-green -rotate-1 mt-1 inline-block">
              SUBMIT A TRACK
            </h3>
            <p className="text-zinc-400 text-sm mt-3">
              Drop something the herd needs to hear. Curb personally approves every track before it hits rotation.
            </p>

            <div className="mt-5 space-y-3">
              <FieldInput
                label="Track name"
                value={form.track_name}
                onChange={(v) => update("track_name", v)}
                placeholder="e.g. Innerbloom"
                required
                testId="submit-track-name"
              />
              <FieldInput
                label="Artist"
                value={form.artist_name}
                onChange={(v) => update("artist_name", v)}
                placeholder="e.g. RÜFÜS DU SOL"
                required
                testId="submit-track-artist"
              />
              <FieldInput
                label="Track link"
                type="url"
                value={form.track_url}
                onChange={(v) => update("track_url", v)}
                placeholder="Spotify / YouTube / SoundCloud / direct MP3"
                required
                testId="submit-track-url"
              />
              <FieldInput
                label="Your name or X handle (optional)"
                value={form.submitter}
                onChange={(v) => update("submitter", v)}
                placeholder="@your_x_handle"
                testId="submit-track-submitter"
              />
            </div>

            <label className="mt-4 flex items-start gap-3 text-sm text-zinc-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.rights_confirmed}
                onChange={(e) => update("rights_confirmed", e.target.checked)}
                data-testid="submit-track-rights-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#14F195]"
                required
              />
              <span>
                I confirm I have the rights to share this track, or it is royalty-free.
              </span>
            </label>

            <button
              type="submit"
              disabled={busy}
              data-testid="submit-track-submit-button"
              className="mt-5 w-full btn-buy font-bebas tracking-widest text-lg rounded-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Send size={16} /> {busy ? "SENDING…" : "SEND TO CURB"}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const FieldInput = ({ label, value, onChange, placeholder, required, type = "text", testId }) => (
  <label className="block">
    <span className="block font-bebas tracking-[0.28em] text-zinc-400 text-xs mb-1">{label}</span>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full rounded-lg bg-[#050409] border border-[#9945FF]/30 text-white placeholder-zinc-600 px-3 py-2 text-sm focus:outline-none focus:border-[#14F195] focus:ring-1 focus:ring-[#14F195]/50 transition-colors"
    />
  </label>
);
